import { useCallback, useMemo, useState } from "react"

import type { Location } from "@be-miniapp/types"
import { useDebouncedValue } from "@be-miniapp/ui/hooks/use-debounced-value"

import type { RideLocation } from "../stores/ride-data"
import type { RideAddressType } from "./ride-route-selection"

export function useSelectRouteMap(params: {
  addressType: RideAddressType
  pickup: RideLocation | undefined
  dropoff: RideLocation | undefined
  defaultPickup?: RideLocation
}) {
  const { addressType, pickup, dropoff, defaultPickup } = params

  const isValidLocation = useCallback((loc: Location | undefined) => {
    if (!loc) return false
    const { latitude, longitude } = loc
    if (latitude === undefined || longitude === undefined) return false
    return !(latitude === 0 && longitude === 0)
  }, [])

  const initialCenter = useMemo<Location | null>(() => {
    const targetLocation = addressType === "pick-up" ? pickup : dropoff
    const candidates = [
      targetLocation?.location,
      pickup?.location,
      defaultPickup?.location,
    ]

    for (const loc of candidates) {
      if (isValidLocation(loc)) return loc as Location
    }

    return null
  }, [
    addressType,
    pickup?.location,
    dropoff?.location,
    defaultPickup?.location,
    isValidLocation,
    dropoff,
    pickup,
  ])

  const [mapCenter, setMapCenter] = useState<Location | null>(initialCenter)
  const [debouncedCenter] = useDebouncedValue(mapCenter, 300)

  const onCenterChange = useCallback((center: { lat: number; lng: number }) => {
    setMapCenter({ latitude: center.lat, longitude: center.lng })
  }, [])

  const getLatLngFromLocation = useCallback(
    (loc: RideLocation | undefined, fallback?: RideLocation) => {
      const lat = loc?.location?.latitude
      const lng = loc?.location?.longitude

      if (lat === undefined || lng === undefined) {
        return fallback ? getLatLngFromLocation(fallback) : undefined
      }

      return { lat, lng }
    },
    []
  )

  const isLocationDataEnabled = useMemo(() => {
    if (debouncedCenter && isValidLocation(debouncedCenter)) return true
    if (defaultPickup?.location && isValidLocation(defaultPickup.location))
      return true
    return false
  }, [debouncedCenter, defaultPickup?.location, isValidLocation])

  return {
    mapCenter,
    setMapCenter,
    debouncedCenter,
    initialCenter,
    onCenterChange,
    getLatLngFromLocation,
    isLocationDataEnabled,
  }
}
